import React from 'react';
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogContent,
  DialogBody,
  DialogActions,
  Button,
  Text,
  Spinner,
  makeStyles,
} from '@fluentui/react-components';
import { Warning24Filled } from '@fluentui/react-icons';
import { UserDeletionImpact } from '../services/api.ts';

const MOBILE = '@media (max-width: 768px)';

const useStyles = makeStyles({
  actions: {
    flexWrap: 'wrap',
    gap: 'var(--space-sm)',
  },
  warningRow: {
    display: 'flex',
    gap: 'var(--space-sm)',
    alignItems: 'flex-start',
    padding: 'var(--space-md)',
    backgroundColor: 'rgba(196, 49, 75, 0.08)',
    borderLeft: '4px solid var(--color-error)',
    borderRadius: 'var(--radius-sm)',
    marginBottom: 'var(--space-md)',
  },
  warningIcon: {
    color: 'var(--color-error)',
    flexShrink: 0,
  },
  list: {
    listStyle: 'none',
    padding: 0,
    margin: '0 0 var(--space-md) 0',
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    gap: '4px var(--space-md)',
    [MOBILE]: {
      gap: '4px var(--space-sm)',
    },
  },
  countCell: {
    fontWeight: 600,
    fontFamily: 'var(--font-mono)',
    color: 'var(--color-text-primary)',
    textAlign: 'right',
    minWidth: '40px',
  },
  labelCell: {
    color: 'var(--color-text-secondary)',
  },
  emptyNote: {
    color: 'var(--color-text-secondary)',
    fontStyle: 'italic',
    marginBottom: 'var(--space-md)',
  },
});

interface DeleteUserDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  impact: UserDeletionImpact | null;
  loading: boolean;          // submitting the delete
  impactLoading: boolean;    // still fetching counts
}

const ITEMS: { key: keyof UserDeletionImpact; label: string }[] = [
  { key: 'assignments', label: 'survey assignment(s)' },
  { key: 'responses', label: 'survey response(s)' },
  { key: 'conversation_messages', label: 'chat history message(s)' },
  { key: 'message_logs', label: 'Telegram message log(s)' },
  { key: 'uploads', label: 'uploaded file(s)' },
  { key: 'transcripts', label: 'voice transcript(s)' },
  { key: 'reminder_preferences', label: 'reminder preference(s)' },
  { key: 'registration_codes', label: 'used registration code(s)' },
];

export default function DeleteUserDialog({
  open,
  onClose,
  onConfirm,
  impact,
  loading,
  impactLoading,
}: DeleteUserDialogProps) {
  const s = useStyles();
  const hasAnyRelated = impact
    ? ITEMS.some(({ key }) => (impact[key] as number) > 0)
    : false;

  return (
    <Dialog open={open} onOpenChange={(_, data) => { if (!data.open && !loading) onClose(); }}>
      <DialogSurface style={{ width: '90vw', maxWidth: '480px' }}>
        <DialogBody>
          <DialogTitle>Permanently delete user?</DialogTitle>
          <DialogContent>
            <div className={s.warningRow}>
              <Warning24Filled className={s.warningIcon} />
              <Text>
                This will permanently delete <strong>{impact?.full_name || 'this user'}</strong> and
                all of their related data. This action cannot be undone.
              </Text>
            </div>

            {impactLoading || !impact ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
                <Spinner size="tiny" />
                <Text size={200}>Calculating impact…</Text>
              </div>
            ) : !hasAnyRelated ? (
              <Text className={s.emptyNote}>
                This user has no related data — only the user record will be removed.
              </Text>
            ) : (
              <>
                <Text size={300} weight="semibold" style={{ display: 'block', marginBottom: '8px' }}>
                  The following will also be deleted:
                </Text>
                <ul className={s.list}>
                  {ITEMS.filter(({ key }) => (impact[key] as number) > 0).map(({ key, label }) => (
                    <React.Fragment key={key}>
                      <li className={s.countCell}>{impact[key] as number}</li>
                      <li className={s.labelCell}>{label}</li>
                    </React.Fragment>
                  ))}
                </ul>
              </>
            )}
          </DialogContent>
          <DialogActions className={s.actions}>
            <Button appearance="secondary" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              onClick={onConfirm}
              disabled={loading || impactLoading}
              style={{ backgroundColor: 'var(--color-error)', borderColor: 'var(--color-error)' }}
            >
              {loading ? 'Deleting…' : 'Delete permanently'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
